import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EVENT_TYPES, COLOR_KEYS, colorOf } from "@/lib/constants";
import {
  UploadCloud, FileText, Loader2, CheckCircle2, Circle, ArrowLeft, Trash2, Plus,
  ShieldCheck, AlertTriangle, Sparkles,
} from "lucide-react";

const STEPS = ["Reading document", "Finding important dates", "Organizing assignments", "Building your planner"];

export default function UploadFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();
  const [stage, setStage] = useState("upload"); // upload | processing | review | error
  const [dragActive, setDragActive] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [course, setCourse] = useState({});
  const [color, setColor] = useState("blue");
  const [events, setEvents] = useState([]);
  const [gradeCats, setGradeCats] = useState([]);
  const [busy, setBusy] = useState(false);

  const isDemo = user && !user.paid_status; // free users get limited preview handled server-side by course cap

  const handleFile = async (file) => {
    if (!file) return;
    if (busy) return; // prevent duplicate/double-click submissions
    const name = file.name.toLowerCase();
    const ok = name.endsWith(".pdf") || name.endsWith(".docx");
    if (!ok) { toast.error("Please upload a PDF or DOCX file."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large. Max 15MB."); return; }
    if (file.size === 0) { toast.error("That file looks empty. Please choose another."); return; }

    setBusy(true);
    setStage("processing");
    setActiveStep(0);
    const timers = [
      setTimeout(() => setActiveStep(1), 900),
      setTimeout(() => setActiveStep(2), 2000),
      setTimeout(() => setActiveStep(3), 3400),
    ];
    const form = new FormData();
    form.append("file", file);
    try {
      // NOTE: do NOT set Content-Type manually — the browser must add the
      // multipart boundary, otherwise the server can't parse the upload.
      const res = await api.post("/syllabus/extract", form);
      timers.forEach(clearTimeout);
      const data = res.data;
      setCourse(data.course || {});
      setColor(COLOR_KEYS[Math.floor(Math.random() * COLOR_KEYS.length)]);
      setEvents((data.events || []).map((e, i) => ({ ...e, _id: i })));
      setGradeCats(data.grade_categories || []);
      setStage("review");
    } catch (err) {
      timers.forEach(clearTimeout);
      const detail = err?.response?.data?.detail ||
        (err?.message === "Network Error"
          ? "Upload failed — please check your connection and try again."
          : "We couldn't reliably read this syllabus. Try uploading a clearer PDF or a different copy.");
      setErrorMsg(typeof detail === "string" ? detail : "Upload failed. Please try again.");
      setStage("error");
    } finally {
      setBusy(false);
    }
  };

  // Reset the hidden input so selecting the SAME file again still fires onChange.
  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    handleFile(file);
  };

  const onDrop = (e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); };

  const updateEvent = (id, field, value) => setEvents(events.map((e) => e._id === id ? { ...e, [field]: value } : e));
  const removeEvent = (id) => setEvents(events.filter((e) => e._id !== id));
  const addEvent = () => setEvents([...events, { _id: Date.now(), title: "", type: "assignment", date: "", needs_review: false, description: "" }]);

  const createPlanner = async () => {
    if (busy) return; // prevent duplicate courses from double-clicks
    setBusy(true);
    try {
      const payload = {
        course, color,
        events: events.map(({ _id, ...e }) => e),
        grade_categories: gradeCats,
      };
      const res = await api.post("/syllabus/save", payload);
      toast.success("Planner created!");
      navigate("/dashboard");
    } catch (err) {
      setBusy(false);
      if (err?.response?.status === 402) {
        toast.error("Free accounts are limited to one course. Unlock Syllabify to add more.");
        navigate("/upgrade");
      } else {
        toast.error("Could not create planner. Please try again.");
      }
    }
  };

  // ---- UPLOAD ----
  if (stage === "upload") {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-8">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </button>
          <div className="text-center mb-8">
            <Logo size="lg" className="justify-center mb-6" />
            <h1 className="text-3xl font-semibold text-slate-900">Let's organize your semester.</h1>
            <p className="mt-2 text-slate-500">Upload a syllabus to get started.</p>
          </div>
          <div
            data-testid="upload-dragdrop-zone"
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed p-10 sm:p-16 text-center cursor-pointer transition-all ${
              dragActive ? "border-blue-500 bg-blue-50/50" : "border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/20"
            }`}>
            <span className="inline-flex items-center justify-center rounded-full bg-blue-50 text-blue-600 p-4 mb-4">
              <UploadCloud className="h-8 w-8" />
            </span>
            <p className="text-slate-900 font-medium">Drag and drop your syllabus here</p>
            <p className="text-slate-400 text-sm my-2">or</p>
            <Button data-testid="choose-file-btn" className="bg-slate-900 hover:bg-slate-800 rounded-lg">Choose File</Button>
            <p className="mt-4 text-xs text-slate-400">PDF or DOCX · up to 15MB</p>
            <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden"
              data-testid="file-input" onChange={onPick} />
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-slate-400" />
            Your syllabus is used only to create your schedule. It is never shared or used to train AI.
          </div>
        </div>
      </div>
    );
  }

  // ---- PROCESSING ----
  if (stage === "processing") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center" data-testid="processing-screen">
          <Logo size="lg" className="justify-center mb-8" />
          <h1 className="text-2xl font-semibold text-slate-900 mb-8">Reading your syllabus…</h1>
          <div className="space-y-4 text-left">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                {i < activeStep ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  : i === activeStep ? <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  : <Circle className="h-5 w-5 text-slate-300" />}
                <span className={i <= activeStep ? "text-slate-900 font-medium" : "text-slate-400"}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- ERROR ----
  if (stage === "error") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center" data-testid="upload-error-screen">
          <span className="inline-flex items-center justify-center rounded-full bg-amber-50 text-amber-600 p-4 mb-5">
            <AlertTriangle className="h-8 w-8" />
          </span>
          <h1 className="text-xl font-semibold text-slate-900">{errorMsg}</h1>
          <p className="mt-2 text-slate-500 text-sm">You can also add events manually after trying again.</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button onClick={() => setStage("upload")} data-testid="try-again-btn" className="bg-slate-900 hover:bg-slate-800 rounded-lg">Try Again</Button>
            <Button variant="outline" onClick={() => { setCourse({}); setEvents([{ _id: 1, title: "", type: "assignment", date: "" }]); setColor("blue"); setStage("review"); }}>Add manually</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- REVIEW ----
  const noDates = events.filter((e) => e.date).length === 0;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">We found these events</h1>
          <p className="mt-2 text-slate-600" data-testid="review-count">
            {events.length > 0
              ? `We found ${events.length} academic event${events.length === 1 ? "" : "s"}. Please review them before continuing.`
              : "We couldn't find events automatically. Add them below."}
          </p>
        </div>

        {noDates && events.length > 0 && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> We couldn't find many dates in this syllabus. Please add or confirm the dates below.
          </div>
        )}

        {/* Course info */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Course</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500">Course name</label>
              <Input value={course.name || ""} onChange={(e) => setCourse({ ...course, name: e.target.value })} placeholder="Introduction to Biology" data-testid="course-name-input" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Course code</label>
              <Input value={course.code || ""} onChange={(e) => setCourse({ ...course, code: e.target.value })} placeholder="BIO 101" data-testid="course-code-input" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Instructor</label>
              <Input value={course.instructor || ""} onChange={(e) => setCourse({ ...course, instructor: e.target.value })} placeholder="Dr. Smith" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Semester</label>
              <Input value={course.semester || ""} onChange={(e) => setCourse({ ...course, semester: e.target.value })} placeholder="Fall 2026" className="mt-1" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs text-slate-500">Course color</label>
            <div className="flex gap-2 mt-2">
              {COLOR_KEYS.map((k) => (
                <button key={k} onClick={() => setColor(k)} data-testid={`color-${k}`}
                  className={`h-7 w-7 rounded-full ${colorOf(k).dot} ${color === k ? "ring-2 ring-offset-2 ring-slate-400" : ""}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Events table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-5">
          <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-400">
            <div className="col-span-4">Event</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-3">Type</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-slate-100">
            {events.map((e) => (
              <div key={e._id} data-testid={`review-row-${e._id}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-4 py-3 items-center">
                <div className="sm:col-span-4">
                  <Input value={e.title} onChange={(ev) => updateEvent(e._id, "title", ev.target.value)} placeholder="Event title" data-testid={`event-title-${e._id}`} className="h-9" />
                </div>
                <div className="sm:col-span-3">
                  <Input type="date" value={e.date || ""} onChange={(ev) => updateEvent(e._id, "date", ev.target.value)} data-testid={`event-date-${e._id}`} className="h-9" />
                </div>
                <div className="sm:col-span-3">
                  <Select value={e.type} onValueChange={(v) => updateEvent(e._id, "type", v)}>
                    <SelectTrigger className="h-9 capitalize" data-testid={`event-type-${e._id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2 flex items-center justify-between sm:justify-end gap-2">
                  {e.needs_review && <span className="text-[10px] font-medium text-amber-600">Review</span>}
                  <button onClick={() => removeEvent(e._id)} data-testid={`delete-event-${e._id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-100">
            <Button variant="outline" onClick={addEvent} data-testid="add-event-btn" className="w-full rounded-lg"><Plus className="h-4 w-4 mr-1" /> Add event</Button>
          </div>
        </div>

        {gradeCats.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Grade weights found</h2>
            <div className="flex flex-wrap gap-2">
              {gradeCats.map((g) => (
                <span key={g.name} className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-sm">{g.name}: {g.weight}%</span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <Button variant="outline" onClick={() => setStage("upload")} className="rounded-lg">Upload different file</Button>
          <Button onClick={createPlanner} disabled={busy} data-testid="create-planner-btn" className="bg-blue-600 hover:bg-blue-700 rounded-lg px-6 py-5">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Create My Planner
          </Button>
        </div>
      </div>
    </div>
  );
}
